// This file is auto-generated by @hey-api/openapi-ts

import {
  activationActivate,
  type Options,
  passwordResetRequest,
  reAuthenticate,
  redeemFunkeOffer,
  registrationRegister,
  requestCancellation,
  requestWithdrawal,
  setSepaPayment,
} from './sdk.gen';
import type {
  ActivationActivateData,
  PasswordResetRequestData,
  ReAuthenticateData,
  RedeemFunkeOfferData,
  RegistrationRegisterData,
  RequestCancellationData,
  RequestWithdrawalData,
  SetSepaPaymentData,
} from './types.gen';

/**
 * Action each reCAPTCHA-gated operation is pinned to server-side, keyed by SDK function name.
 */
export const RECAPTCHA_ACTIONS = {
  passwordResetRequest: 'password_reset',
  reAuthenticate: 're_authenticate',
  activationActivate: 'token_activation',
  setSepaPayment: 'sepa_payment',
  redeemFunkeOffer: 'funke_redeem',
  requestCancellation: 'request_cancellation',
  registrationRegister: 'registration',
  requestWithdrawal: 'request_withdrawal',
} as const;

export type RecaptchaProtectedOperation =
  | 'passwordResetRequest'
  | 'reAuthenticate'
  | 'activationActivate'
  | 'setSepaPayment'
  | 'redeemFunkeOffer'
  | 'requestCancellation'
  | 'registrationRegister'
  | 'requestWithdrawal';

export type RecaptchaAction =
  | 'funke_redeem'
  | 'password_reset'
  | 're_authenticate'
  | 'registration'
  | 'request_cancellation'
  | 'request_withdrawal'
  | 'sepa_payment'
  | 'token_activation';

/**
 * A minted reCAPTCHA token together with the action it was minted for.
 * The action is a type parameter so a token cannot be passed to an operation pinned to a different one.
 */
export type RecaptchaHeaders<A extends RecaptchaAction = RecaptchaAction> = {
  token: string;
  action: A;
};

/**
 * Calls `passwordResetRequest` with a reCAPTCHA token minted for the `password_reset` action.
 * The token is required by the type; its value may be `undefined` when reCAPTCHA is unavailable.
 */
export const passwordResetRequestWithRecaptcha = <ThrowOnError extends boolean = false>(
  options: Options<PasswordResetRequestData, ThrowOnError> & {
    meta: {
      recaptcha: RecaptchaHeaders<'password_reset'> | undefined;
    };
  },
) => passwordResetRequest<ThrowOnError>(options);

/**
 * Calls `reAuthenticate` with a reCAPTCHA token minted for the `re_authenticate` action.
 * The token is required by the type; its value may be `undefined` when reCAPTCHA is unavailable.
 */
export const reAuthenticateWithRecaptcha = <ThrowOnError extends boolean = false>(
  options: Options<ReAuthenticateData, ThrowOnError> & {
    meta: {
      recaptcha: RecaptchaHeaders<'re_authenticate'> | undefined;
    };
  },
) => reAuthenticate<ThrowOnError>(options);

/**
 * Calls `activationActivate` with a reCAPTCHA token minted for the `token_activation` action.
 * The token is required by the type; its value may be `undefined` when reCAPTCHA is unavailable.
 */
export const activationActivateWithRecaptcha = <ThrowOnError extends boolean = false>(
  options: Options<ActivationActivateData, ThrowOnError> & {
    meta: {
      recaptcha: RecaptchaHeaders<'token_activation'> | undefined;
    };
  },
) => activationActivate<ThrowOnError>(options);

/**
 * Calls `setSepaPayment` with a reCAPTCHA token minted for the `sepa_payment` action.
 * The token is required by the type; its value may be `undefined` when reCAPTCHA is unavailable.
 */
export const setSepaPaymentWithRecaptcha = <ThrowOnError extends boolean = false>(
  options: Options<SetSepaPaymentData, ThrowOnError> & {
    meta: {
      recaptcha: RecaptchaHeaders<'sepa_payment'> | undefined;
    };
  },
) => setSepaPayment<ThrowOnError>(options);

/**
 * Calls `redeemFunkeOffer` with a reCAPTCHA token minted for the `funke_redeem` action.
 * The token is required by the type; its value may be `undefined` when reCAPTCHA is unavailable.
 */
export const redeemFunkeOfferWithRecaptcha = <ThrowOnError extends boolean = false>(
  options: Options<RedeemFunkeOfferData, ThrowOnError> & {
    meta: {
      recaptcha: RecaptchaHeaders<'funke_redeem'> | undefined;
    };
  },
) => redeemFunkeOffer<ThrowOnError>(options);

/**
 * Calls `requestCancellation` with a reCAPTCHA token minted for the `request_cancellation` action.
 * The token is required by the type; its value may be `undefined` when reCAPTCHA is unavailable.
 */
export const requestCancellationWithRecaptcha = <ThrowOnError extends boolean = false>(
  options: Options<RequestCancellationData, ThrowOnError> & {
    meta: {
      recaptcha: RecaptchaHeaders<'request_cancellation'> | undefined;
    };
  },
) => requestCancellation<ThrowOnError>(options);

/**
 * Calls `registrationRegister` with a reCAPTCHA token minted for the `registration` action.
 * The token is required by the type; its value may be `undefined` when reCAPTCHA is unavailable.
 */
export const registrationRegisterWithRecaptcha = <ThrowOnError extends boolean = false>(
  options: Options<RegistrationRegisterData, ThrowOnError> & {
    meta: {
      recaptcha: RecaptchaHeaders<'registration'> | undefined;
    };
  },
) => registrationRegister<ThrowOnError>(options);

/**
 * Calls `requestWithdrawal` with a reCAPTCHA token minted for the `request_withdrawal` action.
 * The token is required by the type; its value may be `undefined` when reCAPTCHA is unavailable.
 */
export const requestWithdrawalWithRecaptcha = <ThrowOnError extends boolean = false>(
  options: Options<RequestWithdrawalData, ThrowOnError> & {
    meta: {
      recaptcha: RecaptchaHeaders<'request_withdrawal'> | undefined;
    };
  },
) => requestWithdrawal<ThrowOnError>(options);
